// Settings › Plan and usage (Console D; the mock's S.plan; D107, D108). The head names the plan and
// offers Pro; the strip has the plan, the period's sessions and both retentions; then the
// period's meter with its pace, what the trial's end does (or what the plan includes), the plans
// side by side from GET /plans (plan_entitlements and Pro's prices) with the organization's own
// column marked, and the spend cap and billing, where the owner subscribes and every Stripe flow
// lives (Billing.tsx). A trial is 14 days of Pro; at its end the organization falls to Free and
// nothing stops. Nothing here invents a number: what the API does not answer is left out.
import { useQuery } from '@tanstack/react-query'
import { type FormEvent, type ReactNode, useEffect, useId, useState } from 'react'
import { isApiError } from '../api/client'
import { billingQuery, orgQuery, plansQuery, usageQuery, useSetSpendCap } from '../api/queries'
import type { Organization, Plan, PlanInfo, PlanPrice, Usage } from '../api/types'
import { Btn, PageHead, Section, Strip, type StripItem } from '../components/d'
import { ErrorState, Loading } from '../components/states'
import { PLAN_KEY, trialState } from '../components/TrialMeter'
import { type MessageId, type Translator, useI18n } from '../i18n'
import { compact as compactCount, day } from '../lib/format'
import { BillingSection, ENDED, openBillingPortal } from './Billing'
import { FairUse } from './FairUse'

const DAY = 86_400_000
/** Where "Talk to us" writes (the site's contact address). */
const CONTACT = 'hello@apsio.io'

type Shown = Exclude<Plan, 'trial'>
const NAME: Record<Shown, MessageId> = {
  free: 'plans.free',
  pro: 'plans.pro',
  enterprise: 'plans.enterprise',
}

/** Days as the mock writes them: "14 days", "13 months" from 365 on. */
export function retention(t: Translator['t'], days: number): string {
  return days >= 365
    ? t('plans.months', { months: Math.round(days / 30.4) })
    : t('plans.days', { days })
}

/**
 * Where the period is heading: the sessions so far at the same daily pace to its end, once a
 * day of it has passed; null before (too early to say) or without a period.
 */
export function pace(usage: Pick<Usage, 'period' | 'captured_sessions'>, now: number) {
  if (!usage.period) return null
  const start = Date.parse(usage.period.start)
  const end = Date.parse(usage.period.end)
  const elapsed = Math.min(now, end) - start
  if (elapsed < DAY || end <= start) return null
  return {
    sessions: Math.round((usage.captured_sessions / elapsed) * (end - start)),
    end: usage.period.end,
    day: Math.min(Math.ceil(elapsed / DAY), Math.round((end - start) / DAY)),
    days: Math.round((end - start) / DAY),
    share: Math.min(1, elapsed / (end - start)),
  }
}

export function PlanTab({ orgId, crumb, title }: { orgId: string; crumb: string; title: string }) {
  const i18n = useI18n()
  const { t, date } = i18n
  const org = useQuery(orgQuery(orgId))
  const usage = useQuery(usageQuery(orgId))
  const plans = useQuery(plansQuery)
  const billing = useQuery(billingQuery(orgId))
  const [now] = useState(() => Date.now())

  if (org.isPending || plans.isPending)
    return (
      <main id="main" className="pg se-pg plan">
        <PageHead crumb={crumb} title={title} />
        <Loading />
      </main>
    )
  if (org.isError || plans.isError)
    return (
      <main id="main" className="pg se-pg plan">
        <PageHead crumb={crumb} title={title} />
        <ErrorState error={org.error ?? plans.error} />
      </main>
    )

  const o = org.data
  const rows = plans.data.plans
  const free = rows.find((p) => p.plan === 'free')
  const pro = rows.find((p) => p.plan === 'pro')
  const trial = trialState(o, now)
  const owner = o.role === 'owner'
  const sub = billing.data?.subscription
  const subscribed = !!sub && !ENDED.includes(sub.status)
  // Billing off (503) hides subscribing; with it on, an owner without a subscription subscribes.
  const canSubscribe = owner && billing.data !== null && !billing.isPending && !subscribed

  const meta = [
    o.name,
    t(PLAN_KEY[o.plan]),
    ...(trial && o.trial_ends_at
      ? [t('plans.endsOn', { date: date(o.trial_ends_at, { month: 'short', day: 'numeric' }) })]
      : []),
  ].join(' · ')
  const actions = canSubscribe ? (
    <a className="btn solid" href="#billing">
      {t('plans.choosePro')}
    </a>
  ) : owner && subscribed ? (
    <Btn onClick={() => openBillingPortal(orgId)}>{t('billing.manage')}</Btn>
  ) : null

  return (
    <main id="main" className="pg se-pg plan">
      <PageHead crumb={crumb} title={title} meta={meta} actions={actions} />
      <Strip
        label={t('plans.strip')}
        items={strip(i18n, o, usage.data, trial, { free, pro }, sub)}
      />
      <div className="grid2">
        <UsageSection usage={usage} trial={!!trial} now={now} />
        {trial && free ? (
          <TrialEnd endsAt={o.trial_ends_at} free={free} />
        ) : o.plan === 'free' && free ? (
          <OnFree free={free} />
        ) : (
          <ServiceLevel plan={o.plan} />
        )}
      </div>
      {usage.data ? <FairUse usage={usage.data} /> : null}
      <Section
        title={t('plans.title')}
        meta={t('plans.sub', { brl: brlMonth(i18n, plans.data.pro_prices) })}
      >
        <PlansTable
          plans={rows}
          prices={plans.data.pro_prices}
          current={o.plan}
          trialLeft={trial ? trial.left : null}
          canSubscribe={canSubscribe}
        />
        <p className="se-hint" style={{ paddingTop: 10 }}>
          {t('billing.retention')}
        </p>
      </Section>
      <div className="grid-h">
        <SpendCap orgId={orgId} usage={usage.data} owner={owner} />
        <BillingSection orgId={orgId} role={o.role} />
      </div>
    </main>
  )
}

function strip(
  i18n: Translator,
  o: Organization,
  usage: Usage | undefined,
  trial: ReturnType<typeof trialState>,
  rows: { free: PlanInfo | undefined; pro: PlanInfo | undefined },
  sub: { period_end: string; cancel_at_period_end: boolean } | null | undefined,
): StripItem[] {
  const { t, date } = i18n
  const limit = usage ? (usage.cap?.sessions ?? (usage.included_sessions || null)) : null
  const items: StripItem[] = [
    {
      dt: t('plans.strip.plan'),
      dd: t(o.plan === 'trial' ? 'plan.trial' : NAME[o.plan as Shown]),
      d: trial
        ? trial.ended
          ? t('trial.ended')
          : t('plans.strip.daysLeft', {
              days: trial.left,
              date: o.trial_ends_at
                ? date(o.trial_ends_at, { month: 'short', day: 'numeric' })
                : '',
            })
        : sub
          ? // The same words as Billing's row: the next invoice, or the end when it cancels.
            t(sub.cancel_at_period_end ? 'billing.endsOn' : 'billing.nextInvoiceOn', {
              date: day(i18n, sub.period_end),
            })
          : t('plans.strip.noSub'),
    },
  ]
  if (usage)
    items.push({
      dt: t('plans.strip.sessions'),
      dd: compact(i18n, usage.captured_sessions),
      small: limit ? t('plans.strip.of', { limit: compact(i18n, limit) }) : undefined,
      d: [
        usage.percent !== null ? t('plans.strip.used', { percent: usage.percent }) : null,
        usage.capped ? t('plans.strip.capped') : null,
      ]
        .filter(Boolean)
        .join(' · '),
    })
  const other = (pick: (p: PlanInfo) => number) =>
    o.plan === 'pro' || o.plan === 'enterprise'
      ? rows.free
        ? t('plans.strip.onFree', { value: retention(t, pick(rows.free)) })
        : undefined
      : rows.pro
        ? t('plans.strip.onPro', { value: retention(t, pick(rows.pro)) })
        : undefined
  items.push(
    {
      dt: t('plans.strip.detail'),
      dd: String(o.retention_days),
      small: t('plans.strip.daysUnit'),
      d: other((p) => p.detail_retention_days),
    },
    {
      dt: t('plans.strip.crash'),
      dd:
        o.crash_retention_days >= 365
          ? String(Math.round(o.crash_retention_days / 30.4))
          : String(o.crash_retention_days),
      small: t(o.crash_retention_days >= 365 ? 'plans.strip.monthsUnit' : 'plans.strip.daysUnit'),
      d: other((p) => p.crash_retention_days),
    },
  )
  return items
}

function UsageSection({
  usage,
  trial,
  now,
}: {
  usage: ReturnType<typeof useQuery<Usage>>
  trial: boolean
  now: number
}) {
  const i18n = useI18n()
  const { t, date } = i18n
  if (usage.isPending)
    return (
      <Section title={t('plans.usage')}>
        <Loading />
      </Section>
    )
  if (usage.isError || !usage.data)
    return (
      <Section title={t('plans.usage')}>
        <ErrorState error={usage.error} />
      </Section>
    )
  const u = usage.data
  const limit = u.cap?.sessions ?? (u.included_sessions || null)
  const ahead = pace(u, now)
  const short = (s: string) => date(s, { month: 'short', day: 'numeric' })
  const meta = u.period
    ? t(trial ? 'plans.usage.trial' : 'plans.usage.period', {
        start: short(u.period.start),
        end: short(u.period.end),
      })
    : undefined
  const share = limit ? Math.min(1, u.captured_sessions / limit) : null
  return (
    <Section title={t('plans.usage')} meta={meta}>
      <div className="se-meter">
        <div className="r">
          <span>
            <b>{compact(i18n, u.captured_sessions)}</b>{' '}
            {limit ? t('plans.usage.of', { limit: compact(i18n, limit) }) : t('plans.usage.only')}
          </span>
          {ahead ? <span>{t('plans.usage.day', { day: ahead.day, days: ahead.days })}</span> : null}
        </div>
        {share !== null ? (
          // biome-ignore lint/a11y/useSemanticElements: <meter> cannot hold the day marker, the mock's <s>
          <span
            className={share > 0.7 ? 'meter-bar warn' : 'meter-bar'}
            role="meter"
            aria-valuenow={u.captured_sessions}
            aria-valuemin={0}
            aria-valuemax={limit ?? undefined}
            aria-label={t('plans.strip.sessions')}
          >
            <i style={{ width: `${Math.max(1, share * 100)}%` }} />
            {ahead ? (
              <s
                style={{ left: `${ahead.share * 100}%` }}
                title={t('plans.usage.day', { day: ahead.day, days: ahead.days })}
              />
            ) : null}
          </span>
        ) : null}
        {ahead ? (
          <div className="r">
            <span />
            <span>
              {t('plans.usage.pace', {
                sessions: compact(i18n, ahead.sessions),
                date: short(ahead.end),
              })}
            </span>
          </div>
        ) : null}
      </div>
      <p className="se-hint" style={{ paddingTop: 10 }}>
        {u.cap?.kind === 'hard'
          ? t('plans.usage.hardCap', { cap: compact(i18n, u.cap.sessions) })
          : u.cap?.kind === 'spend'
            ? t('plans.usage.spendCap')
            : t('plans.usage.overage', { included: compact(i18n, u.included_sessions) })}
      </p>
      <ReplayMeter replay={u.replay} />
    </Section>
  )
}

/** The second billed unit (D110): the period's sessions with replay, against its cap or the included ones. */
function ReplayMeter({ replay }: { replay: Usage['replay'] }) {
  const i18n = useI18n()
  const { t } = i18n
  const limit = replay.cap ?? (replay.included || null)
  const share = limit ? Math.min(1, replay.sessions / limit) : null
  return (
    <>
      <div className="se-meter">
        <div className="r">
          <span>
            <b>{compact(i18n, replay.sessions)}</b>{' '}
            {limit
              ? t('plans.usage.replayOf', { limit: compact(i18n, limit) })
              : t('plans.usage.replayOnly')}
          </span>
        </div>
        {share !== null ? (
          // biome-ignore lint/a11y/useSemanticElements: the mock's bar, as the sessions meter's
          <span
            className={share > 0.7 ? 'meter-bar warn' : 'meter-bar'}
            role="meter"
            aria-valuenow={replay.sessions}
            aria-valuemin={0}
            aria-valuemax={limit ?? undefined}
            aria-label={t('plans.replay')}
          >
            <i style={{ width: `${Math.max(1, share * 100)}%` }} />
          </span>
        ) : null}
      </div>
      <p className="se-hint" style={{ paddingTop: 10 }}>
        {replay.capped
          ? t('plans.usage.replayCapped')
          : replay.cap !== null
            ? t('plans.usage.replayHardCap', { cap: compact(i18n, replay.cap) })
            : t('plans.usage.replayOverage', { included: compact(i18n, replay.included) })}
      </p>
    </>
  )
}

/** The trial's end, in D107's words: it falls to Free, and nothing stops. */
function TrialEnd({ endsAt, free }: { endsAt: string | null; free: PlanInfo }) {
  const i18n = useI18n()
  const { t, date } = i18n
  return (
    <Section
      title={t('plans.trialEnd')}
      meta={endsAt ? date(endsAt, { month: 'short', day: 'numeric' }) : undefined}
    >
      <ul className="rl">
        <li>
          <span>{t('plans.trialEnd.free')}</span>
          <span />
        </li>
        <li>
          <span>
            {t('plans.trialEnd.limits', {
              sessions: compact(i18n, free.included_sessions),
              members: free.max_members ?? 0,
              projects: free.max_projects ?? 0,
            })}
          </span>
          <span />
        </li>
        <li>
          <span>{t('plans.trialEnd.retention', { days: free.detail_retention_days })}</span>
          <span />
        </li>
        <li>
          <span>{t('plans.trialEnd.nothingBilled')}</span>
          <span />
        </li>
      </ul>
    </Section>
  )
}

/** What Free includes, beside the meter, for an organization on it. */
function OnFree({ free }: { free: PlanInfo }) {
  const i18n = useI18n()
  const { t } = i18n
  return (
    <Section title={t('plans.onFree')}>
      <ul className="rl">
        <li>
          <span>{t('plans.sessions')}</span>
          <span>
            {t('plans.onFree.sessions', { sessions: compact(i18n, free.included_sessions) })}
          </span>
        </li>
        <li>
          <span>{t('plans.members')}</span>
          <span>{free.max_members ?? t('plans.unlimited')}</span>
        </li>
        <li>
          <span>{t('plans.projects')}</span>
          <span>{free.max_projects ?? t('plans.unlimited')}</span>
        </li>
        <li>
          <span>{t('plans.detail')}</span>
          <span>{retention(t, free.detail_retention_days)}</span>
        </li>
      </ul>
      <p className="se-hint" style={{ paddingTop: 10 }}>
        {t('plans.onFree.hint')}
      </p>
    </Section>
  )
}

/** D108: the service levels of a paid plan, beside the meter. */
function ServiceLevel({ plan }: { plan: Plan }) {
  const { t } = useI18n()
  return (
    <Section title={t('plans.sla')} meta={t(PLAN_KEY[plan])}>
      <ul className="rl">
        <li>
          <span>{t('plans.sla.ingest')}</span>
          <span>{plan === 'enterprise' ? t('plans.byContract') : '99.9 %'}</span>
        </li>
        <li>
          <span>{t('plans.sla.console')}</span>
          <span>{plan === 'enterprise' ? t('plans.byContract') : '99.5 %'}</span>
        </li>
      </ul>
      <p className="se-hint" style={{ paddingTop: 10 }}>
        {t('plans.sla.hint')}
      </p>
    </Section>
  )
}

function money({ number }: Translator, cents: number, currency: PlanPrice['currency']): string {
  return number(cents / 100, {
    style: 'currency',
    currency: currency.toUpperCase(),
    maximumFractionDigits: 0,
  })
}

function priceOf(
  prices: PlanPrice[],
  currency: PlanPrice['currency'],
  interval: PlanPrice['interval'],
) {
  return prices.find((p) => p.currency === currency && p.interval === interval)
}

function brlMonth(i18n: Translator, prices: PlanPrice[]): string {
  const p = priceOf(prices, 'brl', 'month')
  return p ? money(i18n, p.unit_amount, 'brl') : '—'
}

/** Free, Pro and Enterprise side by side; a trial is Pro's column, with its days left. */
function PlansTable({
  plans,
  prices,
  current,
  trialLeft,
  canSubscribe,
}: {
  plans: PlanInfo[]
  prices: PlanPrice[]
  current: Plan
  trialLeft: number | null
  canSubscribe: boolean
}) {
  const i18n = useI18n()
  const { t, number } = i18n
  const mine: Shown = current === 'trial' ? 'pro' : current
  const cur = (p: PlanInfo) => (p.plan === mine ? 'cur' : '')
  const usdMonth = priceOf(prices, 'usd', 'month')
  const usdYear = priceOf(prices, 'usd', 'year')
  const cell = (p: PlanInfo, i: number, value: ReactNode) => (
    <td
      key={p.plan}
      className={[cur(p), i === plans.length - 1 ? 'last' : ''].join(' ').trim() || undefined}
    >
      {value}
    </td>
  )
  const row = (label: string, value: (p: PlanInfo) => ReactNode) => (
    <tr>
      <td className="f">{label}</td>
      {plans.map((p, i) => cell(p, i, value(p)))}
    </tr>
  )
  const overage = plans.find((p) => p.plan === 'pro')?.overage_tiers?.[0]
  return (
    <div className="scroll-x">
      <table className="t se-cmp" aria-label={t('plans.title')}>
        <thead>
          <tr>
            <th className="f">
              <span className="vh">{t('plans.what')}</span>
            </th>
            {plans.map((p, i) => (
              <th
                key={p.plan}
                scope="col"
                className={
                  [cur(p), i === plans.length - 1 ? 'last' : ''].join(' ').trim() || undefined
                }
              >
                <b>{t(NAME[p.plan as Shown])}</b>{' '}
                {p.plan === mine ? (
                  <>
                    {current === 'trial' && trialLeft !== null
                      ? t('plans.currentTrial', { days: trialLeft })
                      : t('plans.currentCol')}
                    {/* Pro's price stays in sight on its own column: a trial is Pro's column. */}
                    {p.plan === 'pro' && usdMonth ? (
                      <span className="pr">
                        {t('plans.perMonth', { price: money(i18n, usdMonth.unit_amount, 'usd') })}
                      </span>
                    ) : null}
                  </>
                ) : p.plan === 'free' ? (
                  t('plans.head.free')
                ) : p.plan === 'pro' && usdMonth ? (
                  t('plans.perMonth', { price: money(i18n, usdMonth.unit_amount, 'usd') })
                ) : (
                  t('plans.head.enterprise')
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {row(t('plans.for'), (p) => t(`plans.for.${p.plan}` as MessageId))}
          {row(t('plans.sessions'), (p) =>
            p.plan === 'enterprise'
              ? t('plans.volume')
              : p.session_cap !== null && p.plan !== 'pro'
                ? t('plans.sessionsCapped', { sessions: compact(i18n, p.session_cap) })
                : overage
                  ? t('plans.sessionsIncluded', {
                      sessions: compact(i18n, p.included_sessions),
                      price: number(overage.cents_per_thousand / 100, {
                        style: 'currency',
                        currency: 'USD',
                        minimumFractionDigits: 2,
                      }),
                    })
                  : compact(i18n, p.included_sessions),
          )}
          {row(t('plans.replay'), (p) => {
            const tier = p.replay_overage_tiers?.[0]
            return p.plan === 'enterprise'
              ? t('plans.volume')
              : p.replay_cap !== null
                ? t('plans.replayCapped', { sessions: compact(i18n, p.replay_cap) })
                : tier
                  ? t('plans.replayIncluded', {
                      sessions: compact(i18n, p.included_replay_sessions),
                      price: number(tier.cents_per_thousand / 100, {
                        style: 'currency',
                        currency: 'USD',
                        minimumFractionDigits: 2,
                      }),
                    })
                  : compact(i18n, p.included_replay_sessions)
          })}
          {row(t('plans.members'), (p) =>
            p.max_members === null
              ? t(p.plan === 'enterprise' ? 'plans.unlimitedSso' : 'plans.unlimited')
              : number(p.max_members),
          )}
          {row(t('plans.projects'), (p) =>
            p.max_projects === null ? t('plans.unlimited') : number(p.max_projects),
          )}
          {row(t('plans.detail'), (p) =>
            p.plan === 'enterprise' ? t('plans.byContract') : retention(t, p.detail_retention_days),
          )}
          {row(t('plans.crash'), (p) =>
            p.plan === 'enterprise' ? t('plans.byContract') : retention(t, p.crash_retention_days),
          )}
          {row(t('plans.sql'), (p) =>
            p.queries_per_minute > 0
              ? t('plans.sqlPerMinute', { queries: p.queries_per_minute })
              : t('plans.sqlNone'),
          )}
          {row(t('plans.sla'), (p) =>
            p.plan === 'free'
              ? t('plans.slaNone')
              : p.plan === 'pro'
                ? t('plans.slaPro')
                : t('plans.byContract'),
          )}
          {row(t('plans.yearly'), (p) =>
            p.plan === 'pro' && usdYear
              ? t('plans.yearlyPrice', { price: money(i18n, usdYear.unit_amount, 'usd') })
              : '–',
          )}
        </tbody>
        <tfoot>
          <tr>
            <td className="f" />
            {plans.map((p, i) =>
              cell(
                p,
                i,
                p.plan === 'pro' && canSubscribe ? (
                  <a className="btn sm solid" href="#billing">
                    {t('plans.choosePro')}
                  </a>
                ) : p.plan === 'enterprise' ? (
                  <a className="btn sm" href={`mailto:${CONTACT}?subject=Apsio%20Enterprise`}>
                    {t('plans.talk')}
                  </a>
                ) : null,
              ),
            )}
          </tr>
        </tfoot>
      </table>
    </div>
  )
}

/** Pro's spend cap (D54): the most a period may cost, in US dollars; owners set it. */
function SpendCap({
  orgId,
  usage,
  owner,
}: {
  orgId: string
  usage: Usage | undefined
  owner: boolean
}) {
  const { t } = useI18n()
  const id = useId()
  const set = useSetSpendCap(orgId)
  const initial = usage?.spend_cap_cents != null ? String(usage.spend_cap_cents / 100) : ''
  const [value, setValue] = useState(initial)
  useEffect(() => setValue(initial), [initial])
  const editable = owner && !!usage?.spend_cap_available
  function submit(e: FormEvent) {
    e.preventDefault()
    const dollars = value.trim() === '' ? null : Number(value)
    if (dollars !== null && (!Number.isFinite(dollars) || dollars < 0)) return
    set.mutate(dollars === null ? null : Math.round(dollars * 100))
  }
  const error = set.error
    ? isApiError(set.error, 402) || isApiError(set.error, 400)
      ? t('plans.spendCap.invalid')
      : t('state.failed')
    : undefined
  return (
    <Section title={t('plans.spendCap')} meta={t('plans.pro')}>
      <form onSubmit={submit} className="sections" style={{ gap: 8 }}>
        <label className="lbl" htmlFor={id}>
          {t('plans.spendCap.label')}
          <input
            id={id}
            className="input mono"
            inputMode="decimal"
            value={value}
            disabled={!editable || set.isPending}
            placeholder={t('plans.spendCap.none')}
            onChange={(e) => setValue(e.target.value)}
          />
        </label>
        {editable ? (
          <div>
            <Btn type="submit" disabled={set.isPending}>
              {t('plans.spendCap.save')}
            </Btn>
          </div>
        ) : null}
        {error ? (
          <p role="alert" className="notice error">
            {error}
          </p>
        ) : null}
      </form>
      <p className="se-hint" style={{ paddingTop: 8 }}>
        {t(editable ? 'plans.spendCap.hint' : 'plans.spendCap.hintOff')}
      </p>
    </Section>
  )
}

/** Counts as Console D writes them: 14.2k, 50k (the mock and the sidebar's meter). */
function compact(i18n: Translator, n: number): string {
  return compactCount(i18n, n).replace(/K$/, 'k')
}
